import type { Worksheet } from "exceljs";
import { SUBHEADER_FILL, THIN_BORDER } from "../../dailyPrintout/render/cellStyle";
import { DATE_ROW_FONT_SIZE, ENTRY_LINE_HEIGHT_PT, WEEK_COLUMN_COUNT } from "../constants";
import type { WeekBlock, WeekStaffScheduleExportColors } from "../types";
import { entryLines, writeEntryCell } from "./entryCell";

type RenderWeekBlockArgs = {
	worksheet: Worksheet;
	block: WeekBlock;
	startRow: number;
	colors: WeekStaffScheduleExportColors;
};

/**
 * Render one week: a tinted date row followed by the week's entry rows. Returns the next free row.
 *
 * Blocks are separated by a heavier top border on the date row rather than a blank spacer row — a
 * profile with years of history would otherwise pay an extra printed row per week.
 */
export const renderWeekBlock = ({ worksheet, block, startRow, colors }: RenderWeekBlockArgs): number => {
	for (let column = 0; column < WEEK_COLUMN_COUNT; column++) {
		const cell = worksheet.getCell(startRow, column + 1);
		cell.value = block.dateCells[column];
		cell.font = { bold: true, size: DATE_ROW_FONT_SIZE, color: { argb: colors.dateRowFontArgb } };
		cell.fill = SUBHEADER_FILL;
		cell.border = { ...THIN_BORDER, top: { style: "medium" } };
		cell.alignment = { horizontal: "center", vertical: "middle" };
	}

	block.entryRows.forEach((entryRow, rowIndex) => {
		const rowNumber = startRow + 1 + rowIndex;
		for (let column = 0; column < WEEK_COLUMN_COUNT; column++) {
			writeEntryCell({
				cell: worksheet.getCell(rowNumber, column + 1),
				entry: entryRow[column],
				isFirstEntryRow: rowIndex === 0,
				colors,
			});
		}
		// Reserve height for the busiest cell in this row, so a stacked time/location/job stays visible
		// even when the column is too narrow to show them side by side.
		const maxLines = Math.max(1, ...entryRow.map((entry) => (entry ? entryLines(entry).length : 1)));
		worksheet.getRow(rowNumber).height = maxLines * ENTRY_LINE_HEIGHT_PT;
	});

	return startRow + 1 + block.entryRows.length;
};
